import { useEffect, useState } from "react";
import { toast } from "sonner";
import BrandHeader from "../../components/BrandHeader";
import { personalYearApi } from "../../lib/api";

// Admin CRUD for the `personal_year_meanings` collection. Each row is keyed
// by the personal-year number (1-9). PATCH endpoint upserts so the panel can
// also seed a missing number without a data-migration.
export default function PersonalYearMeanings() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // { number, title, text, crystal_tip }
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    personalYearApi
      .list()
      .then((r) => setRows(r))
      .catch(() => toast.error("Could not load personal-year meanings"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const byNumber = new Map(rows.map((r) => [r.number, r]));

  const openEdit = (n) => {
    const existing = byNumber.get(n) || { number: n, title: "", text: "", crystal_tip: "" };
    setEditing({ ...existing });
  };

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const updated = await personalYearApi.update(editing.number, {
        title: editing.title || "",
        text: editing.text || "",
        crystal_tip: editing.crystal_tip || "",
      });
      setRows((prev) => {
        const has = prev.some((r) => r.number === updated.number);
        return has
          ? prev.map((r) => (r.number === updated.number ? updated : r))
          : [...prev, updated].sort((a, b) => a.number - b.number);
      });
      toast.success(`Personal Year ${editing.number} saved`);
      setEditing(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen" data-testid="personal-year-page">
      <BrandHeader adminMode />
      <div className="max-w-4xl mx-auto px-5 md:px-10 py-10">
        <div className="mb-6">
          <div className="ph-overline">Content Library</div>
          <h1 className="font-serif text-4xl text-[#2C0A5E] mt-2">Personal Year Meanings</h1>
          <p className="text-sm text-[#6B5E7E] mt-1">
            One card per Personal Year (1-9). Shows as the &ldquo;Your Year Ahead&rdquo; card on the customer dashboard when no active transit matches.
          </p>
        </div>

        {loading ? (
          <div className="text-sm text-[#6B5E7E]">Loading…</div>
        ) : (
          <div className="grid gap-3">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => {
              const r = byNumber.get(n);
              const missing = !r;
              return (
                <div
                  key={n}
                  className="rounded-xl p-4 flex gap-4 items-start"
                  style={{
                    background: "white",
                    border: `1px solid ${missing ? "#E0A030" : "rgba(106,13,173,0.15)"}`,
                    boxShadow: "0 2px 8px rgba(106,13,173,0.05)",
                  }}
                  data-testid={`py-row-${n}`}
                >
                  <div
                    className="shrink-0 w-12 h-12 rounded-full flex items-center justify-center font-serif text-2xl"
                    style={{ background: "rgba(212,168,85,0.15)", color: "#8B6914", border: "1px solid #D4A855" }}
                  >
                    {n}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-serif text-lg text-[#2C0A5E]">{r?.title || (missing ? "— Not yet written —" : "")}</div>
                    <p className="text-sm text-[#4B3A6E] mt-1 line-clamp-3" style={{ display: "-webkit-box", WebkitBoxOrient: "vertical", WebkitLineClamp: 3, overflow: "hidden" }}>
                      {r?.text || (missing ? "This number has no meaning entered yet." : "")}
                    </p>
                    {r?.crystal_tip && (
                      <p className="text-xs italic text-[#8B6914] mt-1">Crystal tip: {r.crystal_tip}</p>
                    )}
                  </div>
                  <button
                    className="ph-btn-ghost shrink-0"
                    onClick={() => openEdit(n)}
                    data-testid={`py-edit-${n}`}
                  >
                    {missing ? "Write" : "Edit"}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {editing && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-4"
          style={{ background: "rgba(20, 5, 40, 0.6)" }}
          onClick={() => !saving && setEditing(null)}
          data-testid="py-edit-modal"
        >
          <div
            className="w-full max-w-2xl rounded-2xl p-6"
            style={{ background: "#FAF8F4", border: "1px solid rgba(212,168,85,0.5)", maxHeight: "90vh", overflowY: "auto" }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="font-serif text-2xl text-[#2C0A5E]">Personal Year {editing.number}</h2>
            <p className="text-xs text-[#6B5E7E] mt-1 mb-4">Edits publish instantly — no deploy needed.</p>

            <label className="block mb-3">
              <span className="text-xs text-[#6B5E7E] uppercase tracking-wider">Title</span>
              <input
                className="ph-input mt-1"
                value={editing.title || ""}
                onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                placeholder="A Year of…"
                data-testid="py-form-title"
              />
            </label>

            <label className="block mb-3">
              <span className="text-xs text-[#6B5E7E] uppercase tracking-wider">Meaning (main body)</span>
              <textarea
                className="ph-input mt-1"
                rows={6}
                value={editing.text || ""}
                onChange={(e) => setEditing({ ...editing, text: e.target.value })}
                placeholder="Plain-language description of what this Personal Year brings…"
                data-testid="py-form-text"
              />
            </label>

            <label className="block mb-4">
              <span className="text-xs text-[#6B5E7E] uppercase tracking-wider">Crystal Tip (optional)</span>
              <input
                className="ph-input mt-1"
                value={editing.crystal_tip || ""}
                onChange={(e) => setEditing({ ...editing, crystal_tip: e.target.value })}
                placeholder="Carry X to support this year's energy…"
                data-testid="py-form-crystal-tip"
              />
            </label>

            <div className="flex justify-end gap-2 mt-2">
              <button className="ph-btn-ghost" onClick={() => setEditing(null)} disabled={saving} data-testid="py-cancel">
                Cancel
              </button>
              <button className="ph-btn-primary" onClick={save} disabled={saving} data-testid="py-save">
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
